import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Clock,
  Cloud,
  Copy,
  Download,
  ExternalLink,
  FilePenLine,
  FileSpreadsheet,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  FolderUp,
  Headphones,
  Link2,
  Loader2,
  RefreshCw,
  Search,
  Share2,
  ShieldCheck,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { PortalChip } from '../../arkorbit-ui/PortalChip';
import {
  PORTAL_DOCUMENT_UPLOAD_URL,
  PORTAL_DOCUMENT_FOLDER_URL,
  PORTAL_NEXTCLOUD_SHARE_IMPORT_URL,
  PORTAL_PROTOKOLLE_COLLECTION,
  getPortalNextcloudShareAllowedHosts,
  getPortalPocketBaseUrl,
  getPortalPbToken,
  getPortalTenantN8nExtras,
  hasPortalAuthzToken,
  portalAuthzHeaders,
  portalAuthzSidecarExpected,
  resolveOnlyofficeIssueJwtUrl,
  validateNextcloudSharePasteUrl,
} from '../../../constants';
import { PORTAL_SHELL } from '../portalTheme';
import { createBlankDocxFile } from '../blankDocx';
import {
  PORTAL_DOCUMENT_FILE_ACCEPT,
  type PortalDocumentOpenFields,
  type PortalProtokoll,
  type PortalRecentDocument,
  canOpenDocumentInPortal,
  classifyPortalDocumentGroup,
  fileExtensionFromName,
  formatDeAtDateTime,
  formatPortalFileSize,
  portalDocumentTypeLabel,
  isAudioFile,
  isPortalDocxForEditor,
  isPortalPdfDocument,
  parsePortalDocumentN8nResponse,
  resolveProtokollDocumentUrl,
  portalDocumentRelativeFolder,
  sanitizePortalFolderInput,
  buildPortalDocumentFolderTree,
  portalFolderDisplayPath,
  portalUploadFolderPathFromWebkit,
  filterPortalFolderUploadFiles,
  isPortalFolderDataTransfer,
  portalDragMayAcceptExternalFiles,
  collectPortalDropFiles,
  loadPortalVirtualFolders,
  savePortalVirtualFolders,
} from '../portalDocuments';
import { PortalAktenwaechterPanel } from '../PortalAktenwaechterPanel';

function resolvePortalDocumentIcon(ext: string): {
  Icon: typeof FileText;
  colorClass: string;
} {
  switch (ext) {
    case 'pdf':
      return { Icon: FileText, colorClass: 'text-red-400' };
    case 'xlsx':
    case 'xls':
    case 'csv':
      return { Icon: FileSpreadsheet, colorClass: 'text-emerald-400' };
    case 'pptx':
    case 'ppt':
      return { Icon: FileText, colorClass: 'text-orange-400' };
    case 'docx':
    case 'doc':
    case 'odt':
      return { Icon: FilePenLine, colorClass: 'text-blue-400' };
    default:
      return { Icon: FileText, colorClass: 'text-slate-400' };
  }
}

export interface DokumenteViewProps {
  customerId: string;
  /** PB `customers.kanzlei` — Co-Brand-Header „… für {KANZLEI_NAME}“ in blankDocx. */
  kanzleiName?: string;
  /** PB `customers.status` — FIELD_DEMO / FIRMA_DEMO → n8n tenant=field|demo. */
  customerStatus?: string;
  /** PB `customers.email` — Fallback für Firma-Demo ohne status-Feld (orbis-demo@…). */
  customerEmail?: string;
  isPremium: boolean;
  protokolle: PortalProtokoll[];
  protokolleLoading: boolean;
  protokolleError: string;
  recentDocs: PortalRecentDocument[];
  /** Zuletzt gewählter Ordner bleibt beim Wechsel in Chat/Editor erhalten. */
  initialFolder?: string;
  onCurrentFolderChange?: (folder: string) => void;
  onRefresh: () => void | Promise<void>;
  /** Direkt nach Upload/NC-Import — Hub-Liste optimistisch ergänzen. */
  onDocumentIngested?: (fields: PortalDocumentOpenFields) => void;
  onOpenDocument: (fields: PortalDocumentOpenFields) => void;
  onRouteAudioToDiktat: (file: File) => void;
  onRecentDocsChange?: () => void;
  /** PB-Record hervorheben (z. B. nach Diktat-Upload). */
  highlightRecordId?: string | null;
  onHighlightClear?: () => void;
  /** Mandanten-PB-Token für Aktenwächter (Phase 1.2). */
  authToken?: string | null;
  completedByEmail?: string;
}

export const DokumenteView: React.FC<DokumenteViewProps> = ({
  customerId,
  kanzleiName,
  customerStatus,
  customerEmail,
  isPremium,
  protokolle,
  protokolleLoading,
  protokolleError,
  recentDocs,
  initialFolder,
  onCurrentFolderChange,
  onRefresh,
  onDocumentIngested,
  onOpenDocument,
  onRouteAudioToDiktat,
  onRecentDocsChange,
  highlightRecordId,
  onHighlightClear,
  authToken,
  completedByEmail,
}) => {
  const ncShareAllowedHosts = useMemo(() => getPortalNextcloudShareAllowedHosts(), []);
  const canPortalDocumentUpload = Boolean(PORTAL_DOCUMENT_UPLOAD_URL);
  const canPortalNcShareImport = Boolean(
    PORTAL_NEXTCLOUD_SHARE_IMPORT_URL && ncShareAllowedHosts.size > 0,
  );

  const [ingestTab, setIngestTab] = useState<'upload' | 'nextcloud'>('upload');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [ncShareUrlInput, setNcShareUrlInput] = useState('');
  const [ncShareBusy, setNcShareBusy] = useState(false);
  const [ingestMsg, setIngestMsg] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState<'alle' | 'word' | 'pdf' | 'protokolle' | 'archiv'>('alle');
  const [manualRefreshing, setManualRefreshing] = useState(false);
  const [currentFolder, setCurrentFolder] = useState(() =>
    sanitizePortalFolderInput(initialFolder || ''),
  );
  const [newFolderName, setNewFolderName] = useState('');
  const [folderBusy, setFolderBusy] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [virtualFolders, setVirtualFolders] = useState<string[]>(() =>
    loadPortalVirtualFolders(customerId),
  );
  const [ncFolders, setNcFolders] = useState<string[]>([]);
  const [ncFoldersLoading, setNcFoldersLoading] = useState(false);
  const [draggingDocId, setDraggingDocId] = useState<string | null>(null);
  const [dropTargetFolder, setDropTargetFolder] = useState<string | null>(null);
  const [movingDocId, setMovingDocId] = useState<string | null>(null);
  const [folderUploadBusy, setFolderUploadBusy] = useState(false);
  const [folderUploadProgress, setFolderUploadProgress] = useState<{ done: number; total: number } | null>(
    null,
  );
  type DocExplorerSortKey = 'name' | 'type' | 'size' | 'date';
  type DocExplorerSortDir = 'asc' | 'desc';
  const [docSortKey, setDocSortKey] = useState<DocExplorerSortKey>('date');
  const [docSortDir, setDocSortDir] = useState<DocExplorerSortDir>('desc');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const manualRefreshBusyRef = useRef(false);

  useEffect(() => {
    setVirtualFolders(loadPortalVirtualFolders(customerId));
  }, [customerId]);

  useEffect(() => {
    savePortalVirtualFolders(customerId, virtualFolders);
  }, [customerId, virtualFolders]);

  useEffect(() => {
    onCurrentFolderChange?.(currentFolder);
  }, [currentFolder, onCurrentFolderChange]);

  type DocFilterKey = typeof groupFilter;

  const DOC_FILTER_CHIPS: { id: DocFilterKey; label: string }[] = [
    { id: 'alle', label: 'Alle' },
    { id: 'word', label: 'Word' },
    { id: 'pdf', label: 'PDF' },
    { id: 'protokolle', label: 'Protokolle' },
    { id: 'archiv', label: 'Archiv' },
  ];

  const matchesDocFilter = useCallback((p: PortalProtokoll): boolean => {
    if (groupFilter === 'alle') return true;
    const group = classifyPortalDocumentGroup(p.nextcloud_path, p.filename);
    const ext =
      fileExtensionFromName(p.filename) ||
      fileExtensionFromName(p.nextcloud_path || '') ||
      fileExtensionFromName(p.nc_url || '');
    if (groupFilter === 'word') {
      return ext === 'doc' || ext === 'docx' || ext === 'odt';
    }
    if (groupFilter === 'pdf') {
      return isPortalPdfDocument(p.filename, p.nextcloud_path, p.nc_url);
    }
    if (groupFilter === 'protokolle') return group === 'other';
    if (groupFilter === 'archiv') return group === 'contracts-final';
    return true;
  }, [groupFilter]);

  const filteredProtokolle = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return protokolle.filter((p) => {
      if (!matchesDocFilter(p)) return false;
      const rel = portalDocumentRelativeFolder(p.nextcloud_path);
      if (rel !== currentFolder) return false;
      if (!q) return true;
      return p.filename.toLowerCase().includes(q);
    });
  }, [protokolle, searchQuery, matchesDocFilter, currentFolder]);

  const childFolders = useMemo(() => {
    const names = new Set<string>();
    const root = buildPortalDocumentFolderTree(protokolle);
    const collect = (parentPath: string) => {
      const node =
        parentPath === ''
          ? root
          : (function find(n: ReturnType<typeof buildPortalDocumentFolderTree>): ReturnType<typeof buildPortalDocumentFolderTree> | null {
              if (n.path === parentPath) return n;
              for (const c of n.children) {
                const hit = find(c);
                if (hit) return hit;
              }
              return null;
            })(root);
      if (!node) return;
      for (const child of node.children) names.add(child.path);
    };
    collect(currentFolder);
    for (const vf of virtualFolders) {
      if (currentFolder) {
        if (vf.startsWith(`${currentFolder}/`)) {
          const rest = vf.slice(currentFolder.length + 1);
          if (rest && !rest.includes('/')) names.add(vf);
        }
      } else if (!vf.includes('/')) {
        names.add(vf);
      }
    }
    for (const nf of ncFolders) {
      if (currentFolder) {
        if (nf.startsWith(`${currentFolder}/`)) {
          const rest = nf.slice(currentFolder.length + 1);
          if (rest && !rest.includes('/')) names.add(nf);
        }
      } else if (!nf.includes('/')) {
        names.add(nf);
      }
    }
    return Array.from(names)
      .sort((a, b) => a.localeCompare(b, 'de'))
      .map((path) => ({
        path,
        name: path.split('/').pop() || path,
      }));
  }, [protokolle, currentFolder, virtualFolders, ncFolders]);

  const folderBreadcrumbs = useMemo(() => {
    if (!currentFolder) return [{ label: 'Alle Dokumente', path: '' }];
    const parts = currentFolder.split('/').filter(Boolean);
    const crumbs: { label: string; path: string }[] = [{ label: 'Alle Dokumente', path: '' }];
    let acc = '';
    for (const part of parts) {
      acc = acc ? `${acc}/${part}` : part;
      crumbs.push({ label: part, path: acc });
    }
    return crumbs;
  }, [currentFolder]);

  const callPortalFolderApi = useCallback(
    async (
      action: 'create' | 'share' | 'move' | 'list',
      folderPath: string,
      extras?: { pb_record_id?: string; sourcePath?: string; targetFolderPath?: string },
    ) => {
      // Firma ohne portal-authz: n8n erwartet X-Arkorbit-Customer-Id oder Upload-Secret;
      // Secret-Cutover-FE sendet nur Bearer → 401 + Console-Noise (Auto-list beim Dokumente-Tab).
      if (!portalAuthzSidecarExpected()) {
        throw new Error('Ordner-API: Auf diesem Portal noch nicht freigeschaltet (AuthZ).');
      }
      if (!hasPortalAuthzToken()) {
        throw new Error('Ordner-API: Bitte neu anmelden (kein gültiges Portal-Token).');
      }
      const url = resolveOnlyofficeIssueJwtUrl(PORTAL_DOCUMENT_FOLDER_URL);
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...portalAuthzHeaders(url),
        },
        credentials: 'omit',
        body: JSON.stringify({
          action,
          folderPath,
          customerId,
          permissions: 19,
          ...getPortalTenantN8nExtras(customerStatus, customerEmail),
          ...extras,
        }),
      });
      let data: Record<string, unknown> = {};
      try {
        data = (await res.json()) as Record<string, unknown>;
      } catch {
        /* ignore */
      }
      if (!res.ok || data.ok === false) {
        const err = typeof data.error === 'string' ? data.error : `HTTP ${res.status}`;
        throw new Error(err);
      }
      return data;
    },
    [customerId, customerStatus, customerEmail],
  );

  const refreshNcFolders = useCallback(
    async (parentFolder: string) => {
      if (!portalAuthzSidecarExpected() || !hasPortalAuthzToken()) {
        setNcFolders([]);
        return;
      }
      setNcFoldersLoading(true);
      try {
        const data = await callPortalFolderApi('list', parentFolder);
        const folders = Array.isArray(data.folders)
          ? data.folders.filter((f): f is string => typeof f === 'string' && Boolean(f.trim()))
          : [];
        setNcFolders(folders);
        if (folders.length) {
          setVirtualFolders((prev) => {
            const merged = new Set([...prev, ...folders]);
            return Array.from(merged);
          });
        }
      } catch {
        /* NC-Liste optional — PB/virtuelle Ordner bleiben nutzbar */
      } finally {
        setNcFoldersLoading(false);
      }
    },
    [callPortalFolderApi],
  );

  useEffect(() => {
    void refreshNcFolders(currentFolder);
  }, [currentFolder, refreshNcFolders]);

  const createFolder = useCallback(async () => {
    const segment = sanitizePortalFolderInput(newFolderName);
    if (!segment) {
      setIngestMsg('Ordnername ungültig oder leer.');
      return;
    }
    const targetPath = currentFolder ? `${currentFolder}/${segment}` : segment;
    setFolderBusy(true);
    setIngestMsg(null);
    try {
      await callPortalFolderApi('create', targetPath);
      setNewFolderName('');
      setVirtualFolders((prev) => (prev.includes(targetPath) ? prev : [...prev, targetPath]));
      setIngestMsg(`Ordner „${segment}" angelegt`);
      await Promise.resolve(onRefresh());
    } catch (err) {
      setIngestMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setFolderBusy(false);
    }
  }, [newFolderName, currentFolder, callPortalFolderApi, onRefresh]);

  const shareCurrentFolder = useCallback(async () => {
    setShareBusy(true);
    setIngestMsg(null);
    try {
      const data = await callPortalFolderApi('share', currentFolder);
      const link = typeof data.share_url === 'string' ? data.share_url : null;
      if (!link) throw new Error('Kein Freigabe-Link erhalten.');
      setShareLink(link);
      setIngestMsg('Freigabe-Link erstellt — zum Mitbearbeiten teilen.');
    } catch (err) {
      setIngestMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setShareBusy(false);
    }
  }, [currentFolder, callPortalFolderApi]);

  const moveDocumentToFolder = useCallback(
    async (doc: PortalProtokoll, targetFolderPath: string) => {
      if (!doc.nextcloud_path?.trim()) {
        setIngestMsg('Dokument hat keinen Nextcloud-Pfad — Verschieben nicht möglich.');
        return;
      }
      const sourceFolder = portalDocumentRelativeFolder(doc.nextcloud_path);
      if (sourceFolder === targetFolderPath) return;
      setMovingDocId(doc.id);
      setIngestMsg(null);
      try {
        await callPortalFolderApi('move', targetFolderPath, {
          pb_record_id: doc.id,
          sourcePath: doc.nextcloud_path,
          targetFolderPath,
        });
        setIngestMsg(`„${doc.filename}" → ${portalFolderDisplayPath(targetFolderPath)}`);
        await Promise.resolve(onRefresh());
      } catch (err) {
        setIngestMsg(err instanceof Error ? err.message : String(err));
      } finally {
        setMovingDocId(null);
        setDraggingDocId(null);
        setDropTargetFolder(null);
      }
    },
    [callPortalFolderApi, onRefresh],
  );

  useEffect(() => {
    if (!highlightRecordId) return;
    const el = rowRefs.current[highlightRecordId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    const timer = window.setTimeout(() => onHighlightClear?.(), 6000);
    return () => window.clearTimeout(timer);
  }, [highlightRecordId, protokolle, onHighlightClear]);

  const sortDocuments = useCallback(
    (items: PortalProtokoll[]) => {
      const dir = docSortDir === 'asc' ? 1 : -1;
      return [...items].sort((a, b) => {
        switch (docSortKey) {
          case 'name':
            return a.filename.localeCompare(b.filename, 'de') * dir;
          case 'type': {
            const ta = portalDocumentTypeLabel(a.filename, a.mime_type);
            const tb = portalDocumentTypeLabel(b.filename, b.mime_type);
            return ta.localeCompare(tb, 'de') * dir;
          }
          case 'size': {
            const sa = a.file_size ?? -1;
            const sb = b.file_size ?? -1;
            return (sa - sb) * dir;
          }
          case 'date':
          default: {
            const da = new Date(a.updated || a.created).getTime();
            const db = new Date(b.updated || b.created).getTime();
            return (da - db) * dir;
          }
        }
      });
    },
    [docSortDir, docSortKey],
  );

  const toggleDocSort = useCallback((key: DocExplorerSortKey) => {
    setDocSortKey((prevKey) => {
      if (prevKey === key) {
        setDocSortDir((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
        return prevKey;
      }
      setDocSortDir(key === 'name' ? 'asc' : 'desc');
      return key;
    });
  }, []);

  const renderSortIcon = (key: DocExplorerSortKey) => {
    if (docSortKey !== key) return <ArrowUpDown className="w-3 h-3 opacity-40" aria-hidden />;
    return docSortDir === 'asc' ? (
      <ArrowUp className="w-3 h-3" aria-hidden />
    ) : (
      <ArrowDown className="w-3 h-3" aria-hidden />
    );
  };

  /** Ordner in der Explorer-Suche mitfiltern (PC-ähnlich). */
  const filteredChildFolders = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return childFolders;
    return childFolders.filter((f) => f.name.toLowerCase().includes(q));
  }, [childFolders, searchQuery]);

  const sortedExplorerFiles = useMemo(
    () => sortDocuments(filteredProtokolle),
    [filteredProtokolle, sortDocuments],
  );

  const explorerHasRows = filteredChildFolders.length > 0 || sortedExplorerFiles.length > 0;

  const handleSelectedFile = useCallback(
    (file: File | null) => {
      if (!file) {
        setUploadFile(null);
        return;
      }
      if (isAudioFile(file)) {
        onRouteAudioToDiktat(file);
        setIngestMsg(`„${file.name}" → Diktat`);
        setUploadFile(null);
        return;
      }
      setUploadFile(file);
      setIngestMsg(null);
    },
    [onRouteAudioToDiktat],
  );

  const handleManualRefresh = useCallback(async () => {
    if (manualRefreshBusyRef.current) return;
    manualRefreshBusyRef.current = true;
    setManualRefreshing(true);
    try {
      await Promise.resolve(onRefresh());
      await refreshNcFolders(currentFolder);
    } finally {
      manualRefreshBusyRef.current = false;
      setManualRefreshing(false);
    }
  }, [onRefresh, refreshNcFolders, currentFolder]);

  const uploadPortalFileCore = useCallback(
    async (file: File, targetFolderOverride?: string) => {
      if (!hasPortalAuthzToken()) {
        throw new Error('Upload: Bitte neu anmelden (kein gültiges Portal-Token).');
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.set('customerId', customerId);
      const folderTarget =
        targetFolderOverride !== undefined
          ? sanitizePortalFolderInput(targetFolderOverride)
          : currentFolder.trim()
            ? sanitizePortalFolderInput(currentFolder)
            : '';
      if (folderTarget) {
        fd.set('folderPath', folderTarget);
      }
      const tenantExtras = getPortalTenantN8nExtras(customerStatus, customerEmail);
      if (tenantExtras.tenant) {
        fd.set('tenant', tenantExtras.tenant);
      }
      const url = resolveOnlyofficeIssueJwtUrl(PORTAL_DOCUMENT_UPLOAD_URL);
      const res = await fetch(url, {
        method: 'POST',
        body: fd,
        credentials: 'omit',
        headers: {
          ...portalAuthzHeaders(url),
        },
      });
      let data: Record<string, unknown> = {};
      try {
        data = (await res.json()) as Record<string, unknown>;
      } catch {
        /* ignore */
      }
      return parsePortalDocumentN8nResponse(data, res);
    },
    [customerId, customerStatus, customerEmail, currentFolder],
  );

  const ingestPortalFile = useCallback(
    async (file: File, targetFolderOverride?: string) => {
      setUploadBusy(true);
      setIngestMsg(null);
      try {
        const fields = await uploadPortalFileCore(file, targetFolderOverride);
        let msg = `„${fields.display_name}" gespeichert`;
        if (!fields.pb_record_id) {
          msg += ' — Index noch nicht verfügbar (Aktualisieren)';
        }
        if (fields.pb_warning) {
          msg += ` — Hinweis: ${fields.pb_warning}`;
        }
        setIngestMsg(msg);
        setUploadFile(null);
        onDocumentIngested?.(fields);
        if (groupFilter !== 'alle' && isPortalPdfDocument(fields.display_name, fields.nextcloud_path, fields.document_url)) {
          setGroupFilter('pdf');
        }
        await Promise.resolve(onRefresh());
        if (!fields.pb_record_id) {
          await new Promise((resolve) => window.setTimeout(resolve, 1200));
          await Promise.resolve(onRefresh());
        }
        if (canOpenDocumentInPortal(isPremium, fields.display_name, fields.nextcloud_path, fields.document_url)) {
          onOpenDocument(fields);
        }
        return fields;
      } catch (err) {
        setIngestMsg(err instanceof Error ? err.message : String(err));
        throw err;
      } finally {
        setUploadBusy(false);
      }
    },
    [uploadPortalFileCore, isPremium, groupFilter, onDocumentIngested, onOpenDocument, onRefresh],
  );

  const uploadDocument = useCallback(async () => {
    if (!uploadFile) return;
    await ingestPortalFile(uploadFile);
  }, [uploadFile, ingestPortalFile]);

  const uploadFolderFiles = useCallback(
    async (fileList: FileList | File[] | null, targetFolderBase?: string) => {
      if (!fileList || !hasPortalAuthzToken()) return;
      const files = filterPortalFolderUploadFiles(fileList);
      if (!files.length) {
        setIngestMsg('Keine gültigen Dateien im Ordner gefunden.');
        return;
      }
      const audioFiles = files.filter((f) => isAudioFile(f));
      const docFiles = files.filter((f) => !isAudioFile(f));
      if (audioFiles.length === 1 && docFiles.length === 0) {
        onRouteAudioToDiktat(audioFiles[0]);
        setIngestMsg(`„${audioFiles[0].name}" → Diktat`);
        return;
      }
      const uploadBase = targetFolderBase !== undefined ? targetFolderBase : currentFolder;
      setFolderUploadBusy(true);
      setFolderUploadProgress({ done: 0, total: docFiles.length });
      setIngestMsg(null);
      let okCount = 0;
      let failCount = 0;
      try {
        for (let i = 0; i < docFiles.length; i++) {
          const file = docFiles[i];
          const rel =
            (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
          const targetFolder = portalUploadFolderPathFromWebkit(rel, uploadBase);
          setFolderUploadProgress({ done: i, total: docFiles.length });
          try {
            const fields = await uploadPortalFileCore(file, targetFolder);
            onDocumentIngested?.(fields);
            okCount += 1;
          } catch {
            failCount += 1;
          }
        }
        await Promise.resolve(onRefresh());
        const parts: string[] = [];
        if (okCount) parts.push(`${okCount} Datei${okCount === 1 ? '' : 'en'} hochgeladen`);
        if (failCount) parts.push(`${failCount} fehlgeschlagen`);
        if (audioFiles.length) parts.push(`${audioFiles.length} Audio → Diktat übersprungen`);
        setIngestMsg(parts.join(' · ') || 'Ordner-Upload abgeschlossen');
      } finally {
        setFolderUploadBusy(false);
        setFolderUploadProgress(null);
        if (folderInputRef.current) folderInputRef.current.value = '';
      }
    },
    [currentFolder, uploadPortalFileCore, onRefresh, onRouteAudioToDiktat, onDocumentIngested],
  );

  const uploadFolder = useCallback(
    async (fileList: FileList | null) => {
      await uploadFolderFiles(fileList);
    },
    [uploadFolderFiles],
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent, targetFolderBase?: string) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const collected = await collectPortalDropFiles(e.dataTransfer);
      if (!collected.length) {
        setIngestMsg('Drop konnte nicht gelesen werden — bitte erneut ablegen oder Datei wählen.');
        return;
      }
      if (
        isPortalFolderDataTransfer(e.dataTransfer) ||
        collected.length > 1 ||
        collected.some((f) => /[/\\]/.test((f as File & { webkitRelativePath?: string }).webkitRelativePath || ''))
      ) {
        void uploadFolderFiles(collected, targetFolderBase);
        return;
      }
      handleSelectedFile(collected[0]);
    },
    [handleSelectedFile, uploadFolderFiles],
  );

  const handleFolderInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      void uploadFolder(e.target.files);
    },
    [uploadFolder],
  );

  const createBlankDocument = useCallback(async () => {
    await ingestPortalFile(createBlankDocxFile(undefined, { kanzleiName }));
  }, [ingestPortalFile, kanzleiName]);

  const handleNextcloudShareImport = useCallback(async () => {
    if (!hasPortalAuthzToken()) {
      setIngestMsg('Nextcloud-Import: Bitte neu anmelden (kein gültiges Portal-Token).');
      return;
    }
    const validated = validateNextcloudSharePasteUrl(ncShareUrlInput, ncShareAllowedHosts);
    if (validated.ok === false) {
      setIngestMsg(validated.error);
      return;
    }
    setNcShareBusy(true);
    setIngestMsg(null);
    try {
      const url = resolveOnlyofficeIssueJwtUrl(PORTAL_NEXTCLOUD_SHARE_IMPORT_URL);
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...portalAuthzHeaders(url),
        },
        credentials: 'omit',
        body: JSON.stringify({
          shareUrl: validated.href,
          customerId,
          ...(getPortalTenantN8nExtras(customerStatus, customerEmail)),
        }),
      });
      let data: Record<string, unknown> = {};
      try {
        data = (await res.json()) as Record<string, unknown>;
      } catch {
        /* ignore */
      }
      const fields = parsePortalDocumentN8nResponse(data, res);
      setNcShareUrlInput('');
      let msg = `„${fields.display_name}" importiert`;
      if (!fields.pb_record_id) {
        msg += ' — Index noch nicht verfügbar (Aktualisieren)';
      }
      setIngestMsg(msg);
      onDocumentIngested?.(fields);
      await Promise.resolve(onRefresh());
      if (canOpenDocumentInPortal(isPremium, fields.display_name, fields.nextcloud_path, fields.document_url)) {
        onOpenDocument(fields);
      }
    } catch (err) {
      setIngestMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setNcShareBusy(false);
    }
  }, [customerId, customerStatus, customerEmail, isPremium, ncShareAllowedHosts, ncShareUrlInput, onDocumentIngested, onOpenDocument, onRefresh]);

  const archiveDocument = useCallback(
    async (recordId: string) => {
      const token = getPortalPbToken();
      if (!token) return;
      if (!confirm('Dokument aus der Liste entfernen? (Ablage in Nextcloud bleibt erhalten.)')) return;
      setArchivingId(recordId);
      try {
        const res = await fetch(
          `${getPortalPocketBaseUrl()}/api/collections/${PORTAL_PROTOKOLLE_COLLECTION}/records/${recordId}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ status: 'archived' }),
          },
        );
        if (res.ok) {
          onRefresh();
          onRecentDocsChange?.();
        }
      } finally {
        setArchivingId(null);
      }
    },
    [onRefresh, onRecentDocsChange],
  );

  const openProtokoll = useCallback(
    (p: PortalProtokoll) => {
      if (!p.nc_url) return;
      const fields: PortalDocumentOpenFields = {
        display_name: p.filename,
        document_url: p.nc_url,
        pb_record_id: p.id,
        nextcloud_path: p.nextcloud_path,
        updated: p.updated,
      };
      if (canOpenDocumentInPortal(isPremium, p.filename, p.nextcloud_path, p.nc_url)) {
        onOpenDocument(fields);
      } else {
        window.open(p.nc_url, '_blank', 'noopener,noreferrer');
      }
    },
    [isPremium, onOpenDocument],
  );

  const explorerColClass =
    'grid grid-cols-[minmax(0,1fr)_4.5rem_4.5rem_7rem_auto] sm:grid-cols-[minmax(0,1fr)_5rem_5rem_8rem_auto] items-center gap-2 px-4 py-2.5';

  const renderFolderRow = (folder: { path: string; name: string }) => (
    <div
      key={`folder:${folder.path}`}
      role="button"
      tabIndex={0}
      onClick={() => {
        setCurrentFolder(folder.path);
        setShareLink(null);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setCurrentFolder(folder.path);
          setShareLink(null);
        }
      }}
      onDragOver={(e) => {
        if (draggingDocId) {
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = 'move';
          setDropTargetFolder(folder.path);
          return;
        }
        if (!canPortalDocumentUpload) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
        setDropTargetFolder(folder.path);
      }}
      onDragLeave={() => {
        if (dropTargetFolder === folder.path) setDropTargetFolder(null);
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const docId = e.dataTransfer.getData('text/plain') || draggingDocId;
        const doc = docId ? protokolle.find((row) => row.id === docId) : undefined;
        if (doc) {
          void moveDocumentToFolder(doc, folder.path);
          setDropTargetFolder(null);
          return;
        }
        if (
          canPortalDocumentUpload &&
          (portalDragMayAcceptExternalFiles(e.dataTransfer) ||
            isPortalFolderDataTransfer(e.dataTransfer))
        ) {
          void handleDrop(e, folder.path);
        }
        setDropTargetFolder(null);
      }}
      className={`${explorerColClass} hover:bg-slate-800/40 transition-colors text-left cursor-pointer ${
        dropTargetFolder === folder.path
          ? 'bg-violet-500/20 ring-2 ring-violet-400/60 ring-inset'
          : ''
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <FolderOpen className="w-4 h-4 text-amber-400 flex-shrink-0" aria-hidden />
        <p className="text-white text-sm font-medium truncate m-0">{folder.name}</p>
      </div>
      <span className="text-[11px] text-slate-500 truncate">Ordner</span>
      <span className="text-[11px] text-slate-600 tabular-nums">—</span>
      <span className="text-[11px] text-slate-600 truncate">—</span>
      <div className="flex items-center justify-end">
        {dropTargetFolder === folder.path ? (
          <span className="text-[11px] text-violet-300 font-medium">Ablegen</span>
        ) : (
          <span className="text-[10px] text-slate-600">Öffnen</span>
        )}
      </div>
    </div>
  );

  const renderDocumentRow = (p: PortalProtokoll) => {
    const ext =
      fileExtensionFromName(p.filename) ||
      fileExtensionFromName(p.nextcloud_path || '') ||
      fileExtensionFromName(p.nc_url || '');
    const { Icon, colorClass } = resolvePortalDocumentIcon(ext);
    const openable = canOpenDocumentInPortal(isPremium, p.filename, p.nextcloud_path, p.nc_url);
    const editorLabel = isPortalDocxForEditor(p.filename, p.nextcloud_path) ? 'Öffnen' : 'Ansehen';
    const typeLabel = portalDocumentTypeLabel(p.filename, p.mime_type);
    const dateLabel = formatDeAtDateTime(p.updated || p.created);
    const sizeLabel = formatPortalFileSize(p.file_size);

    const highlighted = highlightRecordId === p.id;

    return (
      <div
        key={p.id}
        ref={(node) => {
          rowRefs.current[p.id] = node;
        }}
        draggable={Boolean(p.nextcloud_path?.trim()) && movingDocId !== p.id}
        onDragStart={(e) => {
          if (!p.nextcloud_path?.trim()) return;
          setDraggingDocId(p.id);
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', p.id);
        }}
        onDragEnd={() => {
          setDraggingDocId(null);
          setDropTargetFolder(null);
        }}
        className={`${explorerColClass} hover:bg-slate-800/40 transition-colors ${
          highlighted ? 'bg-violet-500/15 ring-1 ring-violet-500/50 ring-inset' : ''
        } ${draggingDocId === p.id ? 'opacity-50' : ''}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={`w-4 h-4 flex-shrink-0 ${colorClass}`} aria-hidden />
          <p className="text-white text-sm font-medium truncate m-0">{p.filename}</p>
        </div>
        <span className="text-[11px] text-slate-500 truncate">{typeLabel}</span>
        <span className="text-[11px] text-slate-500 tabular-nums">{sizeLabel}</span>
        <span className="text-[11px] text-slate-500 truncate flex items-center gap-1">
          <Clock className="w-3 h-3 flex-shrink-0 hidden sm:inline" aria-hidden />
          <span className="truncate">{dateLabel}</span>
        </span>
        <div className="flex items-center gap-1 flex-shrink-0 justify-end">
          {openable ? (
            <button
              type="button"
              onClick={() => openProtokoll(p)}
              className="inline-flex items-center gap-1 rounded-lg bg-violet-600/20 hover:bg-violet-600/30 border border-violet-500/40 text-violet-300 font-medium px-2 py-1 text-[10px] transition-colors"
            >
              <FilePenLine className="w-3 h-3" /> {editorLabel}
            </button>
          ) : null}
          {p.nc_url ? (
            <a
              href={p.nc_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center rounded-lg bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/40 text-blue-400 p-1.5 transition-colors"
              title="Herunterladen"
            >
              <Download className="w-3 h-3" />
            </a>
          ) : null}
          <button
            type="button"
            onClick={() => void archiveDocument(p.id)}
            disabled={archivingId === p.id}
            title="Aus Liste entfernen"
            className="inline-flex items-center rounded-lg p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40"
          >
            {archivingId === p.id ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Trash2 className="w-3 h-3" />
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderDocumentTableHeader = () => (
    <div className="grid grid-cols-[minmax(0,1fr)_4.5rem_4.5rem_7rem_auto] sm:grid-cols-[minmax(0,1fr)_5rem_5rem_8rem_auto] gap-2 px-4 py-2 border-b border-white/[0.06] bg-white/[0.02] text-[10px] font-semibold uppercase tracking-wide text-slate-500">
      <button
        type="button"
        onClick={() => toggleDocSort('name')}
        className="flex items-center gap-1 text-left hover:text-slate-300"
      >
        Name {renderSortIcon('name')}
      </button>
      <button
        type="button"
        onClick={() => toggleDocSort('type')}
        className="flex items-center gap-1 text-left hover:text-slate-300"
      >
        Typ {renderSortIcon('type')}
      </button>
      <button
        type="button"
        onClick={() => toggleDocSort('size')}
        className="flex items-center gap-1 text-left hover:text-slate-300"
      >
        Größe {renderSortIcon('size')}
      </button>
      <button
        type="button"
        onClick={() => toggleDocSort('date')}
        className="flex items-center gap-1 text-left hover:text-slate-300"
      >
        Bearbeitet {renderSortIcon('date')}
      </button>
      <span className="text-right">Aktion</span>
    </div>
  );

  const hasAnyDocuments = protokolle.length > 0;
  const showIngestTabs = canPortalDocumentUpload && canPortalNcShareImport;

  return (
    <div className="space-y-5 animate-fade-in max-w-5xl mx-auto">
      {/* Zone: Hochladen + Nextcloud */}
      {(canPortalDocumentUpload || canPortalNcShareImport) && (
        <div className={`${PORTAL_SHELL.ingestBar}`}>
          <div className={`${PORTAL_SHELL.paneHeader} justify-between gap-2`}>
            <div className="flex items-center gap-2 min-w-0">
              <Upload className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
              <span className="font-semibold text-slate-300">Hinzufügen</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {canPortalDocumentUpload && (!showIngestTabs || ingestTab === 'upload') ? (
                <>
                  <button
                    type="button"
                    onClick={() => folderInputRef.current?.click()}
                    disabled={uploadBusy || folderUploadBusy}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600/20 hover:bg-violet-600/30 disabled:opacity-50 border border-violet-500/40 text-violet-300 font-semibold px-2.5 py-1 text-[11px] transition-colors whitespace-nowrap"
                    title="Kompletten Ordner hochladen (Struktur bleibt erhalten)"
                  >
                    {folderUploadBusy ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <FolderUp className="w-3.5 h-3.5" />
                    )}
                    Ordner hochladen
                  </button>
                  <button
                    type="button"
                    onClick={() => void createBlankDocument()}
                    disabled={uploadBusy || folderUploadBusy}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600/20 hover:bg-blue-600/30 disabled:opacity-50 border border-blue-500/40 text-blue-300 font-semibold px-2.5 py-1 text-[11px] transition-colors whitespace-nowrap"
                    title="Leeres Word-Dokument erstellen und im Editor öffnen"
                  >
                    {uploadBusy ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <FilePenLine className="w-3.5 h-3.5" />
                    )}
                    Neues Dokument
                  </button>
                </>
              ) : null}
            {showIngestTabs && (
              <div className="flex gap-1" role="tablist" aria-label="Quelle">
                <button
                  type="button"
                  role="tab"
                  aria-selected={ingestTab === 'upload'}
                  onClick={() => setIngestTab('upload')}
                  className={`rounded-md px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                    ingestTab === 'upload'
                      ? 'bg-emerald-600/30 text-emerald-200'
                      : 'text-slate-500 hover:text-slate-300'
                  }`}
                >
                  Hochladen
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={ingestTab === 'nextcloud'}
                  onClick={() => setIngestTab('nextcloud')}
                  className={`rounded-md px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                    ingestTab === 'nextcloud'
                      ? 'bg-sky-600/30 text-sky-200'
                      : 'text-slate-500 hover:text-slate-300'
                  }`}
                >
                  Nextcloud
                </button>
              </div>
            )}
            </div>
          </div>

          <div className="p-4 space-y-3">
            {/* DnD critical — do not revert (button→div, Ordner ohne Files-Guard). Fix: 5b210c4 · lessons.md 11.07.2026 */}
            {canPortalDocumentUpload && (!showIngestTabs || ingestTab === 'upload') && (
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = 'copy';
                  setDragOver(true);
                }}
                onDragLeave={(e) => {
                  if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                  setDragOver(false);
                }}
                onDrop={(e) => void handleDrop(e)}
                className={`rounded-xl border-2 border-dashed p-6 transition-colors ${
                  dragOver
                    ? 'border-emerald-500/50 bg-emerald-500/5'
                    : 'border-white/[0.08] hover:border-white/[0.12]'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={PORTAL_DOCUMENT_FILE_ACCEPT}
                  className="hidden"
                  onChange={(e) => handleSelectedFile(e.target.files?.[0] || null)}
                />
                <input
                  ref={folderInputRef}
                  type="file"
                  className="hidden"
                  multiple
                  // @ts-expect-error webkitdirectory ist nicht in allen TS-DOM-Typen
                  webkitdirectory=""
                  onChange={handleFolderInputChange}
                />
                {uploadFile ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-8 h-8 text-emerald-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">{uploadFile.name}</p>
                        <p className="text-slate-500 text-xs">
                          {(uploadFile.size / 1024 / 1024).toFixed(1)} MB
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setUploadFile(null)}
                        className="p-1.5 text-slate-500 hover:text-red-400"
                        aria-label="Entfernen"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void uploadDocument()}
                        disabled={uploadBusy}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-semibold px-3 py-2"
                      >
                        {uploadBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        Speichern
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                    className="w-full flex flex-col items-center gap-2 text-center cursor-pointer"
                  >
                    <Upload className="w-8 h-8 text-slate-600" />
                    <p className="text-slate-300 text-sm">Datei ablegen oder wählen</p>
                    <p className="text-slate-600 text-xs flex items-center gap-2 flex-wrap justify-center">
                      <span>PDF, Word, Tabellen, Text</span>
                      <span className="text-slate-700">·</span>
                      <span className="inline-flex items-center gap-1">
                        <FolderUp className="w-3 h-3" /> Ordner mit Struktur
                      </span>
                      <span className="text-slate-700">·</span>
                      <span className="inline-flex items-center gap-1">
                        <Headphones className="w-3 h-3" /> Audio → Diktat
                      </span>
                    </p>
                  </div>
                )}
              </div>
            )}

            {canPortalNcShareImport && (!showIngestTabs || ingestTab === 'nextcloud') && (
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <input
                  type="url"
                  inputMode="url"
                  value={ncShareUrlInput}
                  onChange={(e) => setNcShareUrlInput(e.target.value)}
                  placeholder="Nextcloud-Freigabe-Link einfügen…"
                  autoComplete="off"
                  className={`flex-1 min-w-0 rounded-lg border border-white/[0.12] bg-slate-900/80 px-3 py-2 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-sky-600/50`}
                />
                <button
                  type="button"
                  onClick={() => void handleNextcloudShareImport()}
                  disabled={ncShareBusy || !ncShareUrlInput.trim()}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-xs font-semibold px-4 py-2 whitespace-nowrap shrink-0"
                >
                  {ncShareBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />}
                  Importieren
                </button>
              </div>
            )}

            {ingestMsg && (
              <p
                className={`text-xs m-0 ${
                  ingestMsg.includes('gespeichert') ||
                  ingestMsg.includes('importiert') ||
                  ingestMsg.includes('hochgeladen') ||
                  ingestMsg.includes('→ Diktat')
                    ? 'text-emerald-400'
                    : 'text-amber-300'
                }`}
              >
                {ingestMsg}
              </p>
            )}
            {folderUploadProgress ? (
              <p className="text-xs text-violet-300 m-0">
                Ordner-Upload: {folderUploadProgress.done}/{folderUploadProgress.total}…
              </p>
            ) : null}
          </div>
        </div>
      )}

      <div className={`${PORTAL_SHELL.ingestBar} overflow-hidden`}>
        <div className={`${PORTAL_SHELL.paneHeader} justify-between gap-2 flex-wrap`}>
          <div className="flex items-center gap-2 min-w-0">
            <Folder className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
            <span className="font-semibold text-slate-300 truncate">
              {portalFolderDisplayPath(currentFolder)}
            </span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              type="button"
              onClick={() => void shareCurrentFolder()}
              disabled={shareBusy}
              className="inline-flex items-center gap-1 rounded-lg bg-violet-600/20 hover:bg-violet-600/30 disabled:opacity-50 border border-violet-500/40 text-violet-300 font-semibold px-2.5 py-1 text-[11px] transition-colors"
              title="Nextcloud-Freigabe für diesen Ordner"
            >
              {shareBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
              Freigeben
            </button>
          </div>
        </div>
        <div className="px-4 py-3 space-y-3 border-b border-white/[0.06]">
          <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
            {folderBreadcrumbs.map((crumb, idx) => (
              <React.Fragment key={crumb.path || 'root'}>
                {idx > 0 ? <span className="text-slate-700">/</span> : null}
                <button
                  type="button"
                  onClick={() => {
                    setCurrentFolder(crumb.path);
                    setShareLink(null);
                  }}
                  className={`hover:text-violet-300 transition-colors ${
                    crumb.path === currentFolder ? 'text-violet-300 font-medium' : ''
                  }`}
                >
                  {crumb.label}
                </button>
              </React.Fragment>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Neuer Ordner…"
              className="flex-1 min-w-0 rounded-lg border border-white/[0.08] bg-slate-900/70 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-violet-500/40"
            />
            <button
              type="button"
              onClick={() => void createFolder()}
              disabled={folderBusy || !newFolderName.trim()}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white text-xs font-semibold px-4 py-2 whitespace-nowrap"
            >
              {folderBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4" />}
              Ordner erstellen
            </button>
          </div>
          {shareLink ? (
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center rounded-lg border border-violet-500/25 bg-violet-500/5 px-3 py-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Link2 className="w-4 h-4 text-violet-300 flex-shrink-0" />
                <span className="text-xs text-slate-300 truncate">{shareLink}</span>
              </div>
              <button
                type="button"
                onClick={() => void navigator.clipboard.writeText(shareLink)}
                className="inline-flex items-center gap-1 rounded-md border border-white/[0.08] px-2 py-1 text-[11px] text-slate-300 hover:bg-white/[0.04]"
              >
                <Copy className="w-3.5 h-3.5" /> Kopieren
              </button>
            </div>
          ) : null}
        </div>
        {/* Suche + Filter im Explorer (Ordner + Dateien) */}
        <div className="px-4 py-3 space-y-2 border-b border-white/[0.06]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600 pointer-events-none" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Ordner und Dateien suchen…"
              aria-label="Dokumente und Ordner durchsuchen"
              className="w-full rounded-lg border border-white/[0.08] bg-slate-900/70 pl-9 pr-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter">
            {DOC_FILTER_CHIPS.map((chip) => (
              <PortalChip
                key={chip.id}
                label={chip.label}
                accent="blue"
                selected={groupFilter === chip.id}
                onClick={() => setGroupFilter(chip.id)}
                className="!px-2.5 !py-1 !text-[11px]"
              />
            ))}
          </div>
        </div>

        {renderDocumentTableHeader()}

        {protokolleLoading && !hasAnyDocuments && filteredChildFolders.length === 0 ? (
          <div className="flex items-center gap-2 px-5 py-4 text-slate-500 text-xs">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Dokumentenliste wird geladen…
          </div>
        ) : ncFoldersLoading && !explorerHasRows ? (
          <div className="flex items-center gap-2 px-5 py-4 text-slate-500 text-xs">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Ordner werden geladen…
          </div>
        ) : explorerHasRows ? (
          <div className="divide-y divide-white/[0.06]">
            {filteredChildFolders.map(renderFolderRow)}
            {sortedExplorerFiles.map(renderDocumentRow)}
          </div>
        ) : (
          <div className="px-5 py-6 text-center text-slate-600 text-xs space-y-1">
            {searchQuery.trim() || groupFilter !== 'alle' ? (
              <>
                <p className="text-slate-400 text-sm m-0">Keine Treffer für Suche oder Filter</p>
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setGroupFilter('alle');
                  }}
                  className="text-xs text-blue-400 hover:text-blue-300"
                >
                  Filter zurücksetzen
                </button>
              </>
            ) : (
              <p className="m-0">
                {currentFolder
                  ? 'Dieser Ordner ist leer — Dateien hochladen oder Unterordner erstellen.'
                  : 'Noch keine Ordner oder Dateien — oben hochladen oder „Ordner erstellen“.'}
              </p>
            )}
          </div>
        )}
        {ncFoldersLoading && explorerHasRows ? (
          <div className="flex items-center gap-2 px-5 py-2 text-slate-500 text-[11px] border-t border-white/[0.06]">
            <Loader2 className="w-3 h-3 animate-spin" /> Ordner-Liste wird aktualisiert…
          </div>
        ) : null}
      </div>

      <PortalAktenwaechterPanel
        customerId={customerId}
        authToken={authToken}
        aktePath={currentFolder || undefined}
        showAktenPicker
        linkableDocuments={protokolle}
        completedByEmail={completedByEmail}
        className="mx-0"
      />

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void handleManualRefresh()}
          disabled={manualRefreshing}
          className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-300 px-2 py-1.5 rounded-lg hover:bg-white/[0.04] transition-colors disabled:opacity-60"
        >
          {manualRefreshing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
          Aktualisieren
        </button>
      </div>

      {protokolleError ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3 text-red-400 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {protokolleError}
        </div>
      ) : null}

      {!hasAnyDocuments && !protokolleLoading && recentDocs.length > 0 ? (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3 text-amber-200/90 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            „Zuletzt geöffnet“ stammt nur aus diesem Browser. Die Server-Liste ist leer — bitte{' '}
            <button type="button" onClick={() => void handleManualRefresh()} className="underline hover:text-amber-100">
              aktualisieren
            </button>{' '}
            oder mit{' '}
            <a href="?forceLogin=1" className="underline hover:text-amber-100">
              ?forceLogin=1
            </a>{' '}
            neu anmelden.
          </span>
        </div>
      ) : null}

      {/* Zone: Zuletzt geöffnet (nur dieser Browser) */}
      {recentDocs.length > 0 ? (
        <div className={`${PORTAL_SHELL.ingestBar} overflow-hidden`}>
          <div className={`${PORTAL_SHELL.paneHeader} justify-between`}>
            <div className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-amber-400" />
              <span className="font-semibold text-slate-300">Zuletzt geöffnet</span>
            </div>
            <span className="text-slate-500 text-xs">{recentDocs.length}</span>
          </div>
          <p className="px-5 pt-2 pb-1 text-[11px] text-slate-600 m-0">
            Nur in diesem Browser — nicht dasselbe wie Ihre gespeicherte Dokumentenliste.
          </p>
          <div className="divide-y divide-white/[0.06]">
            {recentDocs.map((recent) => {
              const pbMatch = recent.pb_record_id
                ? protokolle.find((p) => p.id === recent.pb_record_id)
                : undefined;
              const document_url = resolveProtokollDocumentUrl(protokolle, {
                pb_record_id: recent.pb_record_id,
                document_url: recent.document_url,
              });
              const nextcloud_path = recent.nextcloud_path || pbMatch?.nextcloud_path;
              const ext =
                fileExtensionFromName(recent.display_name) ||
                fileExtensionFromName(recent.nextcloud_path || '');
              const { Icon, colorClass } = resolvePortalDocumentIcon(ext);
              const openable = canOpenDocumentInPortal(
                isPremium,
                recent.display_name,
                recent.nextcloud_path,
                document_url,
              );

              return (
                <button
                  key={`${recent.pb_record_id || recent.document_url}-${recent.openedAt}`}
                  type="button"
                  onClick={() => {
                    if (openable) {
                      onOpenDocument({
                        display_name: recent.display_name,
                        document_url,
                        pb_record_id: pbMatch?.id,
                        nextcloud_path,
                        updated: pbMatch?.updated,
                      });
                    } else if (document_url) {
                      window.open(document_url, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  className="w-full flex items-center justify-between gap-3 px-5 py-3 hover:bg-slate-800/50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Icon className={`w-4 h-4 flex-shrink-0 ${colorClass}`} aria-hidden />
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium truncate">{recent.display_name}</p>
                      <p className="text-slate-500 text-xs mt-0.5">{formatDeAtDateTime(recent.openedAt)}</p>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-slate-500 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="flex items-start gap-2 text-slate-600 text-[11px] leading-relaxed px-1">
        <ShieldCheck className="w-3.5 h-3.5 text-blue-400/70 flex-shrink-0 mt-0.5" />
        <span>
          Gespeicherte Dokumente liegen in Frankfurt (Nextcloud + PocketBase). Zuletzt geöffnet ist nur ein
          Browser-Verlauf. Bei abgelaufenem Link:{' '}
          <a href="mailto:office@arkorbit.at" className="text-blue-400 hover:text-blue-300">
            office@arkorbit.at
          </a>
        </span>
      </div>
    </div>
  );
};

/** Verfahrensmotor Slice 1 — Dokument-Spiegel der Approval-Card (gleiche `card_id` wie Chat). */
export { FristApprovalDocumentMirror } from '../verfahrensmotor/FristApprovalDocumentMirror';
